import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type StripeEnv, createStripeClient } from "@/lib/stripe.server";
import {
  ALLOWED_PRICES,
  TRIAL_ELIGIBLE_PRICES,
  TRIAL_PERIOD_DAYS,
  assertAllowedEnvironment,
  assertAllowedPriceId,
  assertValidStripePriceId,
  assertPriceMatch,
} from "./payments.helpers";

const SUPPORTED_LOCALES = ["fr", "en", "nl"] as const;
type AppLocale = (typeof SUPPORTED_LOCALES)[number];
function normalizeLocale(input: unknown): AppLocale {
  const s = String(input ?? "").toLowerCase().slice(0, 2);
  return (SUPPORTED_LOCALES as readonly string[]).includes(s) ? (s as AppLocale) : "en";
}

async function resolveOrCreateCustomer(
  stripe: ReturnType<typeof createStripeClient>,
  options: { email?: string; userId?: string; locale?: AppLocale },
): Promise<string> {
  if (options.userId && !/^[a-zA-Z0-9_-]+$/.test(options.userId)) {
    throw new Error("Invalid userId");
  }
  if (options.userId) {
    const found = await stripe.customers.search({
      query: `metadata['userId']:'${options.userId}'`,
      limit: 1,
    });
    if (found.data.length) {
      const customer = found.data[0];
      if (options.locale && customer.preferred_locales?.[0] !== options.locale) {
        await stripe.customers.update(customer.id, { preferred_locales: [options.locale] });
      }
      return customer.id;
    }
  }
  if (options.email) {
    const existing = await stripe.customers.list({ email: options.email, limit: 1 });
    if (existing.data.length) {
      const customer = existing.data[0];
      if (options.userId && customer.metadata?.userId !== options.userId) {
        await stripe.customers.update(customer.id, {
          metadata: { ...customer.metadata, userId: options.userId },
          ...(options.locale && { preferred_locales: [options.locale] }),
        });
      } else if (options.locale && customer.preferred_locales?.[0] !== options.locale) {
        await stripe.customers.update(customer.id, { preferred_locales: [options.locale] });
      }
      return customer.id;
    }
  }
  const created = await stripe.customers.create({
    ...(options.email && { email: options.email }),
    ...(options.userId && { metadata: { userId: options.userId } }),
    ...(options.locale && { preferred_locales: [options.locale] }),
  });
  return created.id;
}

type PriceInfo = {
  priceId: string;
  stripePriceId: string;
  amount: number; // in minor units (cents)
  currency: string;
  interval: "month" | "year" | null;
};

// Module-level in-memory cache (per server instance). 5 minute TTL.
const PRICES_TTL_MS = 5 * 60 * 1000;
const pricesCache = new Map<StripeEnv, { at: number; data: PriceInfo[] }>();

export const getPrices = createServerFn({ method: "GET" })
  .inputValidator((data: { environment: StripeEnv }) => {
    return { environment: assertAllowedEnvironment(data?.environment) };
  })
  .handler(async ({ data }): Promise<PriceInfo[]> => {
    const environment = assertAllowedEnvironment(data?.environment);
    try {
      const cached = pricesCache.get(environment);
      if (cached && Date.now() - cached.at < PRICES_TTL_MS) return cached.data;

      const stripe = createStripeClient(environment);
      const lookupKeys = Array.from(ALLOWED_PRICES);
      const res = await stripe.prices.list({ lookup_keys: lookupKeys, active: true });
      const byKey = new Map(res.data.map((p) => [p.lookup_key as string, p]));
      const out: PriceInfo[] = lookupKeys
        .map((key) => {
          const p = byKey.get(key);
          if (!p || p.unit_amount == null) return null;
          return {
            priceId: key,
          stripePriceId: p.id,
            amount: p.unit_amount,
            currency: p.currency,
            interval: (p.recurring?.interval as "month" | "year" | undefined) ?? null,
          };
        })
        .filter((x): x is PriceInfo => x !== null);

      pricesCache.set(environment, { at: Date.now(), data: out });
      return out;
    } catch (error) {
      console.error("[payments] getPrices failed", {
        environment,
        message: error instanceof Error ? error.message : String(error),
      });
      throw new Error("Unable to load prices");
    }
  });

export const createCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    priceId: string;
    stripePriceId?: string;
    returnUrl: string;
    environment: StripeEnv;
    locale?: string;
  }) => {
    assertAllowedPriceId(data.priceId);
    if (data.stripePriceId) assertValidStripePriceId(data.stripePriceId);
    return {
      ...data,
      environment: assertAllowedEnvironment(data.environment),
      locale: normalizeLocale(data.locale),
    };
  })
  .handler(async ({ data, context }) => {
    const { userId, supabase } = context;
    const { data: { user } } = await supabase.auth.getUser();
    const stripe = createStripeClient(data.environment);

    let stripePrice;
    if (data.stripePriceId) {
      stripePrice = await stripe.prices.retrieve(data.stripePriceId);
      assertPriceMatch({
        priceId: data.priceId,
        stripePriceId: data.stripePriceId,
        resolvedLookupKey: stripePrice.lookup_key ?? null,
      });
    } else {
      const prices = await stripe.prices.list({ lookup_keys: [data.priceId], active: true });
      if (!prices.data.length) throw new Error("Price not found");
      stripePrice = prices.data[0];
    }
    const isRecurring = stripePrice.type === "recurring";

    const customerId = await resolveOrCreateCustomer(stripe, {
      email: user?.email ?? undefined,
      userId,
      locale: data.locale,
    });

    const session = await stripe.checkout.sessions.create({
      line_items: [{ price: stripePrice.id, quantity: 1 }],
      mode: isRecurring ? "subscription" : "payment",
      ui_mode: "embedded_page",
      return_url: data.returnUrl,
      customer: customerId,
      locale: data.locale,
      ...({ managed_payments: { enabled: true } } as any),
      metadata: {
        userId,
        managed_payments: "true",
      },
      ...(isRecurring && {
        subscription_data: {
          metadata: { userId },
          ...(TRIAL_ELIGIBLE_PRICES.has(data.priceId) && {
            trial_period_days: TRIAL_PERIOD_DAYS,
          }),
        },
      }),
    } as any);

    return session.client_secret;
  });

export const createPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl?: string; environment: StripeEnv; locale?: string }) => ({
    ...data,
    environment: assertAllowedEnvironment(data.environment),
    locale: normalizeLocale(data.locale),
  }))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: sub, error: subError } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (subError || !sub?.stripe_customer_id) throw new Error("No subscription found");

    const stripe = createStripeClient(data.environment);
    const portal = await stripe.billingPortal.sessions.create({
      customer: sub.stripe_customer_id as string,
      ...(data.returnUrl && { return_url: data.returnUrl }),
      locale: data.locale,
    });
    return portal.url;
  });